import { logoUrlFromBranding, resolveClinicLogoUrl } from '@/lib/clinicBranding';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';
import type { PatientReportSettings } from './model';
import { invoiceLetterheadLines, type PatientReportPdfLetterhead } from './pdf';

export async function loadReportLetterhead(
  settings: PatientReportSettings,
  clinic?: {
    name?: string | null;
    address?: unknown;
    cvr?: string | null;
    phone?: string | null;
    email?: string | null;
    branding?: Json | null;
  } | null,
  override?: 'inherit' | 'none' | 'invoice' | 'custom' | null,
): Promise<PatientReportPdfLetterhead> {
  const mode = override && override !== 'inherit' ? override : settings.letterhead;
  if (mode === 'none') return { mode: 'none' };
  if (mode === 'invoice') {
    return {
      mode: 'invoice',
      lines: invoiceLetterheadLines({
        name: clinic?.name,
        address: clinic?.address,
        cvr: clinic?.cvr,
        phone: clinic?.phone,
        email: clinic?.email,
      }),
      logo: await fetchLogo(clinic?.branding),
    };
  }

  if (mode === 'custom' && settings.letterheadStoragePath) {
    const { data, error } = await supabase.storage
      .from('patient-reports')
      .download(settings.letterheadStoragePath);
    if (error || !data) return { mode: 'none' };
    const bytes = new Uint8Array(await data.arrayBuffer());
    if (settings.letterheadMime === 'application/pdf' || bytes[0] === 0x25) {
      return { mode: 'pdf', bytes };
    }
    return { mode: 'image', bytes };
  }

  return { mode: 'none' };
}

async function fetchLogo(branding: Json | null | undefined): Promise<Uint8Array | null> {
  const stored = logoUrlFromBranding(branding);
  const url = resolveClinicLogoUrl(stored);
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
}
